import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, ShoppingCart, X, ChevronDown, ChevronUp } from "lucide-react";
import api from "../lib/api";
import { useApp } from "../context/AppContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const toAbs = (u) => (u && u.startsWith("/api/") ? `${BACKEND_URL}${u}` : u);

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [productsMap, setProductsMap] = useState({});
  const [expanded, setExpanded] = useState(null);
  const [dlg, setDlg] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const { customer, patchCustomer } = useApp();
  const nav = useNavigate();

  const load = () => api.get("/projects").then(r => setProjects(r.data));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    api.get("/products").then(r => {
      const map = {};
      r.data.forEach(p => { map[p.id] = p; });
      setProductsMap(map);
    }).catch(() => {});
  }, []);

  const create = async (fromCart = false) => {
    if (!name.trim()) { toast.error("Please name your project"); return; }
    setBusy(true);
    try {
      const items = fromCart ? (customer?.cart || []) : [];
      await api.post("/projects", { name: name.trim(), items });
      toast.success(`Project “${name}” saved${fromCart ? ` with ${items.length} item(s)` : ""}`);
      setName("");
      setDlg(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally { setBusy(false); }
  };

  const loadIntoCart = async (p) => {
    if (!p.items || p.items.length === 0) { toast.error("This project has no items"); return; }
    await patchCustomer({ cart: p.items, current_step: "cart" });
    toast.success(`Loaded ${p.items.length} item(s) into your cart`);
    nav("/cart");
  };

  const del = async (p) => {
    if (!window.confirm(`Delete project "${p.name}"?`)) return;
    await api.delete(`/projects/${p.id}`);
    toast.success("Deleted");
    load();
  };

  const removeItem = async (project, idx) => {
    const nextItems = project.items.filter((_, i) => i !== idx);
    await api.patch(`/projects/${project.id}`, { items: nextItems });
    toast.success("Item removed");
    load();
  };

  return (
    <div className="max-w-screen-2xl mx-auto px-6 md:px-12 py-16" data-testid="projects-page">
      <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
        <div>
          <p className="label-eyebrow">My Collections</p>
          <h1 className="font-editorial text-5xl sm:text-6xl tracking-editorial mt-3">Projects</h1>
          <p className="text-sm text-[#5C5C5C] mt-4 max-w-md">Save the tiles you&apos;re considering for a specific room or client. Reload any project into your cart with one click.</p>
        </div>
        <button onClick={() => setDlg(true)} className="btn-clay" data-testid="new-project-btn">
          <Plus className="w-4 h-4" strokeWidth={1.5} /> Create New Project
        </button>
      </div>

      {projects.length === 0 ? (
        <p className="text-center text-[#8A8A8A] py-20" data-testid="projects-empty">No projects yet. Click <em>Create New Project</em> to save your current cart as a project.</p>
      ) : (
        <div className="space-y-4">
          {projects.map(p => {
            const isOpen = expanded === p.id;
            return (
              <div key={p.id} className="border border-[#1A1A1A]/10 bg-white transition-shadow" data-testid={`project-${p.id}`}>
                <button
                  onClick={() => setExpanded(isOpen ? null : p.id)}
                  className="w-full text-left p-6 flex items-start justify-between gap-6 hover:bg-[#F7F5EE] transition-colors"
                  data-testid={`project-toggle-${p.id}`}
                >
                  <div>
                    <p className="label-eyebrow">{new Date(p.created_at).toLocaleDateString()}</p>
                    <h3 className="font-editorial text-2xl mt-1 tracking-editorial">{p.name}</h3>
                    <p className="text-sm text-[#5C5C5C] mt-2">{p.items.length} item{p.items.length !== 1 ? "s" : ""}</p>
                  </div>
                  <div className="flex items-center gap-4">
                    {isOpen ? <ChevronUp className="w-5 h-5 text-[#1E2C5A]" strokeWidth={1.5} /> : <ChevronDown className="w-5 h-5 text-[#1E2C5A]" strokeWidth={1.5} />}
                  </div>
                </button>

                {isOpen && (
                  <div className="border-t border-[#1A1A1A]/10 p-6" data-testid={`project-body-${p.id}`}>
                    {p.items.length === 0 ? (
                      <p className="text-[#8A8A8A] text-sm">This project has no items yet.</p>
                    ) : (
                      <div className="divide-y divide-[#1A1A1A]/10">
                        {p.items.map((it, i) => {
                          const prod = productsMap[it.product_id];
                          return (
                            <div key={it.product_id || `${p.id}-item-${i}`} className="py-4 flex items-center gap-4" data-testid={`project-item-${p.id}-${i}`}>
                              <div className="w-16 h-16 bg-[#F0EFEA] flex-shrink-0 overflow-hidden">
                                {prod?.image_url && <img src={toAbs(prod.image_url)} alt={prod.our_code} className="w-full h-full object-cover" />}
                              </div>
                              <div className="flex-1 min-w-0">
                                {prod ? (
                                  <>
                                    <p className="font-editorial text-lg tracking-editorial">{prod.our_code}</p>
                                    <p className="text-xs text-[#8A8A8A]">{prod.size} · {prod.category}</p>
                                  </>
                                ) : (
                                  <p className="text-sm text-[#8A8A8A] italic">Product no longer available</p>
                                )}
                                <p className="text-xs text-[#5C5C5C] mt-1">
                                  {it.length}ft × {it.width_or_height}ft · <span className="text-[#1E2C5A] font-medium">{it.boxes} box{it.boxes !== 1 ? "es" : ""}</span>
                                  {prod && <span className="text-[#8A8A8A]"> · ₹{(Number(prod.offer_price) * Number(prod.tile_area_sqft || 1) * Number(prod.packing || 1) * Number(it.boxes || 0)).toLocaleString("en-IN")}</span>}
                                </p>
                              </div>
                              <button
                                onClick={() => removeItem(p, i)}
                                className="text-red-700 hover:text-red-900"
                                data-testid={`remove-item-${p.id}-${i}`}
                                aria-label="Remove item"
                              >
                                <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    <div className="flex gap-3 mt-6 flex-wrap">
                      <button onClick={() => loadIntoCart(p)} className="btn-clay" data-testid={`load-project-${p.id}`}>
                        <ShoppingCart className="w-4 h-4" strokeWidth={1.5} /> Load into Cart
                      </button>
                      <button onClick={() => del(p)} className="btn-outline-ink !text-red-700 !border-red-700 hover:!bg-red-700 hover:!text-white" data-testid={`del-project-${p.id}`}>
                        <Trash2 className="w-4 h-4" strokeWidth={1.5} /> Delete Project
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {dlg && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="new-project-dlg">
          <div className="bg-[#FAF9F6] max-w-md w-full p-10 relative fade-up">
            <button onClick={() => setDlg(false)} className="absolute top-4 right-4 text-[#5C5C5C]"><X className="w-5 h-5" strokeWidth={1.5} /></button>
            <p className="label-eyebrow">New Project</p>
            <h2 className="font-editorial text-3xl mt-2 mb-8">Name your project</h2>
            <input
              autoFocus
              className="input-editorial mb-8"
              placeholder="e.g. Villa 12 · Bathroom"
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="new-project-name"
            />
            <div className="flex gap-2 flex-wrap">
              <button onClick={() => create(false)} disabled={busy} className="btn-outline-ink flex-1" data-testid="new-project-empty">
                Create Empty
              </button>
              <button onClick={() => create(true)} disabled={busy || !(customer?.cart || []).length} className="btn-clay flex-1" data-testid="new-project-fromcart">
                Save Current Cart ({(customer?.cart || []).length})
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
